import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { fetchActivity, type ActivityMachine, type ActivityWork } from "../api";
import BackLink from "../components/BackLink";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import { useSession } from "../session";

/** How often the page asks again while it is open. */
const REFRESH_MS = 10_000;

/**
 * AdminActivity answers "is anything happening, and is anything billing?" from
 * one screen (requirement 0065, ADR-0133): what needs attention, the rented
 * machines, and the queue's work grouped by book.
 *
 * It only shows. Stopping work stays where it already lives, on the narration
 * screens. The attention lines arrive in English, written by the server for
 * the owner; everything else is named here.
 */
export default function AdminActivity() {
  const { me, isPending } = useSession();
  const t = useT();
  const isAdmin = !!me?.user.platform_admin;
  const activity = useQuery({
    queryKey: ["admin", "activity"],
    queryFn: fetchActivity,
    enabled: isAdmin,
    refetchInterval: REFRESH_MS,
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;

  const data = activity.data;
  // Ages are measured against the server's clock, not the phone's.
  const now = data ? Date.parse(data.now) : 0;
  const since = (at: string) => Math.max(0, Math.round((now - Date.parse(at)) / 1000));
  const live = data?.machines.filter((m) => m.status !== "stopped") ?? [];
  const stopped = data?.machines.filter((m) => m.status === "stopped") ?? [];
  const books = groupByBook(data?.work ?? []);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/admin">{t.admin.back}</BackLink>
      <header className="mt-4 mb-8">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
          {t.activity.title}
        </h1>
        <p className="mt-1 text-sm text-muted">{t.activity.what}</p>
        {activity.dataUpdatedAt > 0 && (
          <p data-testid="activity-updated" className="mt-1 text-xs text-muted">
            {t.activity.updated(
              new Date(activity.dataUpdatedAt).toLocaleTimeString(t.meta.dateLocale),
            )}
          </p>
        )}
      </header>

      {(isPending || activity.isPending) && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-40 rounded-card" />
        </Pending>
      )}

      {activity.isError && !data && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.activity.loadFailed}{" "}
          <button type="button" onClick={() => void activity.refetch()} className="underline">
            {t.common.tryAgain}
          </button>
        </p>
      )}

      {data && (
        <div className="space-y-8">
          <section data-testid="activity-attention">
            <h2 className="mb-2 text-sm font-semibold text-muted">{t.activity.attention}</h2>
            {data.attention.length === 0 ? (
              <p data-testid="activity-all-clear" className="text-sm text-muted">
                {t.activity.allClear}
              </p>
            ) : (
              <ul className="space-y-2">
                {data.attention.map((line) => (
                  <li
                    key={line}
                    data-testid="activity-attention-line"
                    dir="auto"
                    className="rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
                  >
                    {line}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section data-testid="activity-machines">
            <h2 className="mb-2 text-sm font-semibold text-muted">{t.activity.machines}</h2>
            {live.length === 0 ? (
              <p data-testid="activity-no-machines" className="text-sm text-muted">
                {t.activity.noMachines}
              </p>
            ) : (
              <ul className="space-y-2">
                {live.map((m) => (
                  <MachineRow key={m.id} machine={m} since={since} />
                ))}
              </ul>
            )}
            {stopped.length > 0 && (
              <>
                <h3 className="mt-4 mb-2 text-xs font-semibold text-muted">
                  {t.activity.stoppedToday}
                </h3>
                <ul className="space-y-2">
                  {stopped.map((m) => (
                    <MachineRow key={m.id} machine={m} since={since} />
                  ))}
                </ul>
              </>
            )}
          </section>

          <section data-testid="activity-work">
            <h2 className="mb-2 text-sm font-semibold text-muted">{t.activity.work}</h2>
            {books.length === 0 ? (
              <p data-testid="activity-no-work" className="text-sm text-muted">
                {t.activity.noWork}
              </p>
            ) : (
              <ul className="space-y-3">
                {books.map(({ bookID, title, groups }) => (
                  <li
                    key={bookID ?? "other"}
                    data-testid="activity-book"
                    className="rounded-card border border-line bg-surface"
                  >
                    <div className="border-b border-line px-3 py-2 font-medium">
                      {bookID ? (
                        <Link
                          to={`/studio/books/${bookID}`}
                          dir="auto"
                          data-testid="activity-book-title"
                          className="block truncate hover:underline"
                        >
                          {title}
                        </Link>
                      ) : (
                        <span data-testid="activity-book-title" className="text-muted">
                          {t.activity.other}
                        </span>
                      )}
                    </div>
                    <ul className="divide-y divide-line">
                      {groups.map((g) => (
                        <WorkRow key={`${g.kind}/${g.lane}`} work={g} since={since} />
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </main>
  );
}

function MachineRow({
  machine: m,
  since,
}: {
  machine: ActivityMachine;
  since: (at: string) => number;
}) {
  const t = useT();
  const a = t.activity;
  const up = a.duration(m.up_seconds);
  const billing = m.status !== "stopped";
  return (
    <li
      data-testid="activity-machine"
      className="rounded-card border border-line bg-surface px-3 py-2"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">{a.lane[m.lane] ?? <bdi>{m.lane}</bdi>}</span>
        <span
          data-testid="activity-machine-status"
          className={`text-xs font-medium ${billing ? "text-sage-deep" : "text-muted"}`}
        >
          {a.status[m.status] ?? m.status}
        </span>
      </div>
      <p className="mt-1 text-sm text-muted tabular-nums">
        {billing ? a.up(up) : a.ranFor(up)} ·{" "}
        <span data-testid="activity-machine-cost">
          {billing ? a.soFar(ltr(m.cost)) : a.spent(ltr(m.cost))}
        </span>{" "}
        · {a.perHour(ltr(m.rate))}
      </p>
      {billing ? (
        <p className="text-sm text-muted tabular-nums">
          {m.last_seen_at ? a.seen(a.duration(since(m.last_seen_at))) : a.neverSeen} ·{" "}
          <span data-testid="activity-machine-jobs">{a.jobs(m.running_jobs)}</span>
        </p>
      ) : (
        m.stop_reason && (
          <p dir="auto" className="text-sm text-muted">
            {a.stopReason(m.stop_reason)}
          </p>
        )
      )}
    </li>
  );
}

function WorkRow({ work: g, since }: { work: ActivityWork; since: (at: string) => number }) {
  const t = useT();
  const a = t.activity;
  const counts = [g.running > 0 && a.running(g.running), g.ready > 0 && a.waiting(g.ready)].filter(
    Boolean,
  );
  return (
    <li data-testid="activity-group" className="px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span data-testid="activity-kind">{a.kind[g.kind] ?? <bdi>{g.kind}</bdi>}</span>
        <span className="text-xs text-muted">{a.lane[g.lane] ?? <bdi>{g.lane}</bdi>}</span>
      </div>
      <p className="text-muted tabular-nums">
        <span data-testid="activity-counts">{counts.join(" · ")}</span>
        {g.failed > 0 && (
          <>
            {counts.length > 0 && " · "}
            <span className="text-red-600 dark:text-red-400">{a.failed(g.failed)}</span>
          </>
        )}
        {g.oldest_ready_since && <> · {a.oldest(a.duration(since(g.oldest_ready_since)))}</>}
      </p>
      {g.last_error && (
        <p
          dir="auto"
          className="mt-1 line-clamp-2 text-xs break-words text-red-700 dark:text-red-300"
        >
          {g.last_error}
        </p>
      )}
    </li>
  );
}

/**
 * ltr isolates a left-to-right run, an amount like "$0.89", inside a sentence
 * that may be Persian. Isolating the whole sentence is not enough: resolved
 * right to left, the neutral "$" detaches and lands after the number. The
 * isolate marks sit inside the translated string, where a <bdi> cannot.
 */
function ltr(text: string): string {
  return `\u2066${text}\u2069`;
}

interface BookWork {
  bookID: string | null;
  title: string;
  groups: ActivityWork[];
}

/** The server sends work ordered by book title, "Other" last; keep that order. */
export function groupByBook(work: ActivityWork[]): BookWork[] {
  const out = new Map<string, BookWork>();
  for (const g of work) {
    const key = g.book_id ?? "";
    const book = out.get(key) ?? { bookID: g.book_id, title: g.book_title, groups: [] };
    book.groups.push(g);
    out.set(key, book);
  }
  const books = [...out.values()];
  // "Other" goes last even if the server's order ever changes.
  return [...books.filter((b) => b.bookID), ...books.filter((b) => !b.bookID)];
}
